/**
 * Product-card quick-buy control (design "Product Card, rebuilt" 1a).
 *
 * Every available card shows a round "+" button. Behaviour:
 *  - Single-variant, in-stock product → clicking "+" adds it to the cart with
 *    AJAX (no page reload), opens the cart drawer, and morphs the "+" into a
 *    quantity stepper ( -  qty  + ). The stepper is kept in sync with the cart.
 *  - Multi-variant product → the "+" is a plain link to the product page so the
 *    shopper can choose colour / storage first (we never guess a variant).
 *
 * The stepper only exists for single-variant cards. On load (and after any cart
 * change) we read the cart once and show the stepper on cards whose variant is
 * already in the bag, so the control always reflects reality.
 *
 * Progressive enhancement: the "+" is an <a href="{product}"> in the markup, so
 * with JS off (or if an add fails) it just navigates to the product page.
 *
 * Delegated + re-scanned on section loads, so AJAX-injected cards work too.
 */
(function () {
  "use strict";
  if (window.__pcardQuickBuyInit) return;
  window.__pcardQuickBuyInit = true;

  var routes = window.routes || {};
  var ADD_URL = (routes.cart_add_url || "/cart/add") + ".js";
  var CHANGE_URL = (routes.cart_change_url || "/cart/change") + ".js";
  var CART_URL = (routes.cart_url || "/cart") + ".js";
  var CART_BASE = routes.cart_url || "/cart";
  /* Product-card quick buy never auto-opens the drawer. Requesting the full
     cart-drawer section added ~139 KB of HTML to every mutation even though
     only the header badge is visible. The drawer refreshes authoritatively on
     its next open (cart-drawer-runtime.js). */
  var CART_SECTIONS = ["cart-icon-bubble"];
  var mutationQueue = Promise.resolve();
  var syncVersion = 0;
  var mutationVersion = 0;
  var pendingOptimisticDelta = 0;
  var optimisticMinimumByVariant = Object.create(null);
  var lastCart = null;
  var feedbackTimers = new WeakMap();

  function withCartSections(body) {
    return Object.assign(
      {
        sections: CART_SECTIONS,
        sections_url: window.location.pathname,
      },
      body
    );
  }

  function jsonPost(url, body) {
    return window.cartMutationFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.text().then(function (text) {
        var payload = {};
        try { payload = text ? JSON.parse(text) : {}; } catch (error) {}
        if (!r.ok) {
          var requestError = new Error(
            payload.description || payload.message || payload.errors || (url + " " + r.status)
          );
          requestError.status = r.status;
          requestError.payload = payload;
          throw requestError;
        }
        return payload;
      });
    });
  }

  function isQuantityLimitError(error) {
    var message = String(error && error.message || '');
    return error && error.status === 422 && /maximum|can't add more|cannot add more|already.*cart|quantity|available|stock/i.test(message);
  }

  function showBuyFeedback(buyEl, error) {
    var media = buyEl.closest('.pcard__media, .card__media') || buyEl.parentElement;
    if (!media) return;
    var feedback = media.querySelector('[data-pcard-buy-feedback]');
    if (!feedback) return;
    feedback.textContent = isQuantityLimitError(error)
      ? (buyEl.dataset.maxQuantityMessage || 'Maximum quantity reached')
      : (buyEl.dataset.cartErrorMessage || 'Unable to update cart. Please try again.');
    var currentTimer = feedbackTimers.get(feedback);
    if (currentTimer) clearTimeout(currentTimer);
    feedback.classList.remove('is-visible');
    void feedback.offsetWidth;
    feedback.classList.add('is-visible');
    feedbackTimers.set(feedback, setTimeout(function () {
      feedback.classList.remove('is-visible');
      feedbackTimers.delete(feedback);
    }, 3200));
  }

  function cancelPendingFly() {
    var flyToCart = window.AlAsilFlyToCart;
    if (flyToCart && typeof flyToCart.cancelPending === 'function') flyToCart.cancelPending();
  }

  /* State is CSS-driven off the [data-qty] attribute (see component-pcard.css):
     qty 0 → "+" · qty > 0 → compact cart+count badge, expanding to the −/+ stepper
     on hover. Here we only set the numbers + data-qty. */
  function setQty(buyEl, qty) {
    var qtyEl = buyEl.querySelector("[data-pcard-qty]");
    var countEl = buyEl.querySelector("[data-pcard-count]");
    var stepper = buyEl.querySelector("[data-pcard-stepper]");
    if (qtyEl) qtyEl.textContent = qty;
    if (countEl) countEl.textContent = qty;
    if (stepper) stepper.classList.toggle("is-one", qty === 1);
    buyEl.setAttribute("data-qty", qty);
  }

  function setVariantQty(variantId, qty) {
    document
      .querySelectorAll('[data-pcard-buy][data-variant-id="' + variantId + '"]')
      .forEach(function (el) {
        setQty(el, qty);
      });
  }

  function setVariantBusy(variantId, busy) {
    document
      .querySelectorAll('[data-pcard-buy][data-variant-id="' + variantId + '"]')
      .forEach(function (el) {
        el.classList.toggle('is-busy', busy);
      });
  }

  function getVariantLines(cart, variantId) {
    return (cart && cart.items ? cart.items : []).filter(function (item) {
      return String(item.variant_id) === String(variantId);
    });
  }

  function chooseDecrementLine(lines) {
    if (!lines.length) return null;
    // Prefer the ordinary, property-free line. If every line is customized,
    // decrement the last line deterministically instead of letting Shopify
    // choose an ambiguous row from a variant ID.
    return lines.find(function (item) {
      return Object.keys(item.properties || {}).filter(function (key) {
        return key.charAt(0) !== '_';
      }).length === 0;
    }) || lines[lines.length - 1];
  }

  function decrementVariant(variantId, visibleQuantity) {
    function changeOne(cart) {
      var lines = getVariantLines(cart, variantId);
      var aggregate = lines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
      if (aggregate !== visibleQuantity) return null;
      var target = chooseDecrementLine(lines);
      if (!target) return null;
      return jsonPost(
        CHANGE_URL,
        withCartSections({ id: target.key, quantity: Math.max(0, target.quantity - 1) })
      );
    }

    var immediate = changeOne(lastCart);
    if (immediate) return immediate;

    // A preceding add may still be reconciling its partial response. Refresh
    // only in that exceptional case; normal decrement remains one request.
    return fetchCart().then(function (cart) {
      applyCartQuantities(cart);
      var request = changeOne(cart);
      if (!request) throw new Error('Cart changed before decrement');
      return request;
    });
  }

  function adjustCartBubble(delta) {
    if (!delta) return;
    document
      .querySelectorAll(
        '#cart-icon-bubble .wbhcartitem, #cart-icon-bubble .header__cart-count, [data-cart-count]'
      )
      .forEach(function (node) {
        var current = parseInt((node.textContent || '').trim(), 10) || 0;
        var next = Math.max(0, current + delta);
        node.textContent = next;
        node.classList.toggle('hidden', next === 0);
      });
  }

  function markDrawerStale() {
    window.__alasilCartDrawerStale = true;
    document.dispatchEvent(new CustomEvent('cart:drawer-stale'));
  }

  function applyCartQuantities(cart) {
    lastCart = cart;
    var byVariant = {};
    (cart.items || []).forEach(function (it) {
      byVariant[it.variant_id] = (byVariant[it.variant_id] || 0) + it.quantity;
    });
    document
      .querySelectorAll('[data-pcard-buy][data-single-variant="true"]')
      .forEach(function (el) {
        var vid = parseInt(el.getAttribute("data-variant-id"), 10);
        var authoritativeQty = byVariant[vid] || 0;
        var optimisticMinimum = optimisticMinimumByVariant[vid];
        if (typeof optimisticMinimum === 'number') {
          // Keep the optimistic floor while an add is still in flight. A
          // previously-started cart read can finish after Shopify accepts the
          // add; clearing the floor from that stale response causes a visible
          // 1 -> 0 -> 1 flicker before the post-mutation cart arrives.
          if (pendingOptimisticDelta === 0 && authoritativeQty >= optimisticMinimum) {
            delete optimisticMinimumByVariant[vid];
          } else {
            authoritativeQty = Math.max(authoritativeQty, optimisticMinimum);
          }
        }
        setQty(el, authoritativeQty);
      });
  }

  function fetchCart() {
    if (typeof window.getCartSnapshot === 'function') {
      return window.getCartSnapshot();
    }
    return fetch(CART_URL, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(function (r) {
      if (!r.ok) throw new Error(CART_URL + " " + r.status);
      return r.json();
    });
  }

  function refreshFromCart() {
    var requestedAtVersion = mutationVersion;
    return fetchCart()
      .then(function (cart) {
        if (requestedAtVersion !== mutationVersion) return null;
        applyCartQuantities(cart);
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  function publishUpdate(cartData) {
    cartData = Object.assign({}, cartData, window.cartSnapshotFromState?.(cartData));
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "pcard-quick-buy", cartData: cartData });
    }
  }

  function fetchCartSections() {
    return fetch(CART_BASE + "?sections=cart-icon-bubble", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(function (r) {
      if (!r.ok) throw new Error(CART_BASE + " sections " + r.status);
      return r.json();
    });
  }

  function applyCartBubble(sections) {
    var html = sections["cart-icon-bubble"];
    if (!html) return;
    var tmp = document.createElement("div");
    tmp.innerHTML = html;
    var fresh = tmp.querySelector("#cart-icon-bubble") || tmp.firstElementChild;
    document.querySelectorAll("#cart-icon-bubble").forEach(function (node) {
      if (fresh) node.innerHTML = fresh.innerHTML;
    });
    var countNode = fresh && fresh.querySelector('.wbhcartitem, .header__cart-count');
    if (countNode) {
      var count = (parseInt((countNode.textContent || '').trim(), 10) || 0) + pendingOptimisticDelta;
      if (typeof window.updateCartCount === 'function') window.updateCartCount(count);
    }
  }

  function syncCartUi(mutationData) {
    var version = ++syncVersion;
    var sectionsPromise =
      mutationData && mutationData.sections
        ? Promise.resolve(mutationData.sections)
        : fetchCartSections();
    return sectionsPromise.then(function (sections) {
      if (version !== syncVersion) return mutationData;

      applyCartBubble(sections);
      markDrawerStale();

      // /cart/change.js returns the complete cart. Use it directly instead of
      // paying for a redundant cart.js request after every +/- action.
      if (mutationData && typeof mutationData.item_count === 'number' && mutationData.items) {
        if (pendingOptimisticDelta === 0) applyCartQuantities(mutationData);
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: mutationData } }));
        publishUpdate(Object.assign({ sections: sections, skipDrawerOpen: true }, mutationData));
        return mutationData;
      }

      /* Reconcile all duplicate product cards in the background. This request
         is deliberately not returned: it must never keep the visible control
         busy after Shopify has accepted the mutation and returned the badge. */
      fetchCart()
        .then(function (cart) {
          if (version !== syncVersion) return;
          var state = Object.assign({ sections: sections, skipDrawerOpen: true }, cart);
          if (pendingOptimisticDelta === 0) applyCartQuantities(cart);
          document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: cart } }));
          publishUpdate(state);
        })
        .catch(function () {});

      return mutationData;
    });
  }

  function queueMutation(task) {
    var result = mutationQueue.then(task, task);
    mutationQueue = result.catch(function () {});
    return result;
  }

  document.addEventListener("click", function (e) {
    var add = e.target.closest("[data-pcard-add]");
    var inc = e.target.closest("[data-pcard-inc]");
    var dec = e.target.closest("[data-pcard-dec]");
    if (!add && !inc && !dec) return;

    var buyEl = e.target.closest("[data-pcard-buy]");
    if (!buyEl) return;
    var single = buyEl.getAttribute("data-single-variant") === "true";
    // Keep the id as a STRING - /cart/change.js validates it against
    // /\A\d+\z/ and rejects a JSON number with a 400.
    var vid = buyEl.getAttribute("data-variant-id");

    /* "+" add button */
    if (add) {
      // Multi-variant (or no variant) → let the <a> navigate to the PDP.
      if (!single || !vid) return;
      e.preventDefault();
      if (buyEl.classList.contains("is-busy")) return;
      var previousQty = parseInt(buyEl.getAttribute("data-qty"), 10) || 0;
      setVariantBusy(vid, true);
      mutationVersion += 1;

      setVariantQty(vid, previousQty + 1);
      optimisticMinimumByVariant[vid] = previousQty + 1;
      pendingOptimisticDelta += 1;
      var flyApiPromise = Promise.resolve(
        window.AlAsilFlyToCart ||
        (typeof window.__loadFlyToCart === 'function' ? window.__loadFlyToCart() : null)
      ).catch(function () { return null; });
      queueMutation(function () {
        return jsonPost(
          ADD_URL,
          withCartSections({ items: [{ id: vid, quantity: 1 }] })
        )
          .then(function (mutationData) {
            try { window.AlAsilCartAddContext?.('card', vid, mutationData); } catch (_) {}
            /* Success motion is gated by Shopify's accepted mutation. A 422
               limit response now produces no fly and no fake badge increment;
               the badge changes exactly when the product reaches the cart. */
            return flyApiPromise.then(function (flyToCart) {
              return flyToCart && typeof flyToCart.playPending === 'function'
                ? flyToCart.playPending(add)
                : false;
            }).catch(function () {
              return false;
            }).then(function () {
              adjustCartBubble(1);
              pendingOptimisticDelta -= 1;
              return syncCartUi(mutationData).catch(function () {
                return refreshFromCart();
              });
            });
          }, function (error) {
            pendingOptimisticDelta -= 1;
            cancelPendingFly();
            throw error;
          });
      })
        .then(function () {
          setVariantBusy(vid, false);
          /* No auto-open of the cart drawer - the on-card badge/stepper is the
             feedback; the shopper opens the cart when they choose to. */
        })
        .catch(function (error) {
          setVariantBusy(vid, false);
          delete optimisticMinimumByVariant[vid];
          setVariantQty(vid, previousQty);
          showBuyFeedback(buyEl, error);
        });
      return;
    }

    /* Stepper -/+ (single-variant only) */
    e.preventDefault();
    if (!vid || buyEl.classList.contains("is-busy")) return;
    var cur = parseInt(buyEl.getAttribute("data-qty"), 10) || 0;
    var next = inc ? cur + 1 : cur - 1;
    if (next < 0) next = 0;
    setVariantBusy(vid, true);
    mutationVersion += 1;

    setVariantQty(vid, next); // optimistic across duplicate cards
    pendingOptimisticDelta += next - cur;
    var stepFlyApiPromise = inc
      ? Promise.resolve(
          window.AlAsilFlyToCart ||
          (typeof window.__loadFlyToCart === 'function' ? window.__loadFlyToCart() : null)
        ).catch(function () { return null; })
      : Promise.resolve(null);
    queueMutation(function () {
      return (inc
        ? jsonPost(ADD_URL, withCartSections({ items: [{ id: vid, quantity: 1 }] }))
        : decrementVariant(vid, cur))
        .then(function (mutationData) {
          if (inc) { try { window.AlAsilCartAddContext?.('card', vid, mutationData); } catch (_) {} }
          /* Every successful +1 uses the same success transition as the first
             add. Quantity-limit errors never reach this block, so they still
             produce no false motion or temporary badge increment. */
          return stepFlyApiPromise.then(function (flyToCart) {
            return inc && flyToCart && typeof flyToCart.playPending === 'function'
              ? flyToCart.playPending(inc)
              : false;
          }).catch(function () {
            return false;
          }).then(function () {
            adjustCartBubble(next - cur);
            pendingOptimisticDelta -= next - cur;
            return syncCartUi(mutationData).catch(function () {
              return refreshFromCart();
            });
          });
        }, function (error) {
          pendingOptimisticDelta -= next - cur;
          if (inc) cancelPendingFly();
          throw error;
        });
    })
      .then(function () {
        setVariantBusy(vid, false);
        /* Keep the drawer out of the way on +/- from the card. */
      })
      .catch(function (error) {
        setVariantBusy(vid, false);
        showBuyFeedback(buyEl, error);
        refreshFromCart().then(function (cart) {
          if (!cart) setVariantQty(vid, cur);
        });
      });
  });

  function init() { refreshFromCart(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  document.addEventListener("shopify:section:load", refreshFromCart);
  document.addEventListener("loadmore:complete", refreshFromCart);
  document.addEventListener('cart:external-sync', function (event) {
    if (event.detail && event.detail.cart) applyCartQuantities(event.detail.cart);
    else refreshFromCart();
  });

  // Carousels and lazy sections can insert duplicate product cards after the
  // authoritative cart response was already applied. Reconcile those nodes
  // from the last in-memory cart instead of issuing another network request.
  new MutationObserver(function (mutations) {
    if (!lastCart) return;
    var addedCartControl = mutations.some(function (mutation) {
      return Array.from(mutation.addedNodes || []).some(function (node) {
        return node.nodeType === 1 && (
          node.matches?.('[data-pcard-buy]') || node.querySelector?.('[data-pcard-buy]')
        );
      });
    });
    if (addedCartControl) requestAnimationFrame(function () { applyCartQuantities(lastCart); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* Reflect changes made elsewhere (drawer, other cards) - but ignore our own
     events to avoid a feedback loop. */
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
      if (ev && ev.source === "pcard-quick-buy") return;
      refreshFromCart();
    });
  }
})();
